<template>
  <!--
    COMPOSITOR DE MENSAGEM
    Modal (b-modal) porque escrever é uma tarefa focada, curta e com fim claro.
    O b-modal já traz: prender o foco, Esc para fechar e devolver o foco.

    Ordem dos blocos segue o modelo mental do e-mail: PARA QUEM → O QUÊ → ENVIAR.
  -->
  <b-modal
    id="chat-composer"
    :visible="composer.open"
    size="lg"
    centered
    scrollable
    title-tag="h2"
    header-class="chat-composer__modal-header"
    body-class="chat-composer__modal-body"
    footer-class="chat-composer__modal-footer"
    @change="onVisibleChange"
    @shown="focusFirstField"
  >
    <!-- ── Título ──────────────────────────────────────────────────── -->
    <template #modal-title>
      <span class="chat-composer__title">
        <i
          :class="['fas', replyTo ? 'fa-reply' : 'fa-pen']"
          aria-hidden="true"
        ></i>
        {{ replyTo ? "Responder mensagem" : "Nova mensagem para a equipe" }}
      </span>
    </template>

    <div
      class="chat-composer"
      @keydown.ctrl.enter.prevent="submit"
      @keydown.meta.enter.prevent="submit"
    >
      <!-- ── Contexto da resposta ──────────────────────────────────── -->
      <div v-if="replyTo" class="chat-composer__reply">
        <div class="chat-composer__reply-text">
          <span class="chat-composer__reply-label">
            Respondendo a <strong>{{ replyTo.sender_name }}</strong>
          </span>
          <q class="chat-composer__reply-quote">{{ excerpt(replyTo.body) }}</q>
        </div>
        <button
          type="button"
          class="chat-composer__icon-btn"
          aria-label="Não responder, escrever mensagem nova"
          title="Escrever mensagem nova em vez de responder"
          @click="cancelReply"
        >
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
      </div>

      <!-- ── 1. PARA QUEM ──────────────────────────────────────────── -->
      <fieldset class="chat-composer__section">
        <legend class="chat-composer__legend">
          <span class="chat-composer__step" aria-hidden="true">1</span>
          Para quem?
        </legend>

        <!--
          Dois modos excludentes = radio, não checkbox.
          Cartões grandes: alvo fácil e escolha visível de longe.
        -->
        <div class="chat-composer__modes" role="radiogroup">
          <label
            :class="[
              'chat-composer__mode',
              { 'chat-composer__mode--active': draft.mode === 'PEOPLE' },
            ]"
          >
            <input
              v-model="draft.mode"
              type="radio"
              name="chat-mode"
              value="PEOPLE"
              class="chat-composer__mode-input"
            />
            <i class="fas fa-user-friends" aria-hidden="true"></i>
            <span>
              <strong>Pessoas específicas</strong>
              <small>Escolha um ou mais colegas</small>
            </span>
          </label>

          <label
            :class="[
              'chat-composer__mode',
              'chat-composer__mode--all',
              { 'chat-composer__mode--active': draft.mode === 'ALL' },
            ]"
          >
            <input
              v-model="draft.mode"
              type="radio"
              name="chat-mode"
              value="ALL"
              class="chat-composer__mode-input"
            />
            <i class="fas fa-users" aria-hidden="true"></i>
            <span>
              <strong>Toda a equipe</strong>
              <small>
                {{
                  recipients.length
                    ? `${recipients.length} pessoas ativas`
                    : "Todos os usuários ativos"
                }}
              </small>
            </span>
          </label>
        </div>

        <!-- Aviso de impacto: enviar para todos interrompe todo mundo -->
        <p
          v-if="draft.mode === 'ALL'"
          class="chat-composer__notice chat-composer__notice--warning"
        >
          <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
          Todos receberão um aviso na tela, inclusive quem está em atendimento.
          Use para comunicados gerais.
        </p>

        <!-- ── Seleção de pessoas ── -->
        <div v-else class="chat-composer__people">
          <!-- Selecionados primeiro: confirma a escolha sem rolar a lista -->
          <div
            v-if="selectedUsers.length"
            class="chat-composer__chips"
            aria-label="Destinatários selecionados"
            role="group"
          >
            <span
              v-for="user in selectedUsers"
              :key="user.id"
              class="chat-composer__chip"
            >
              {{ user.name }}
              <button
                type="button"
                class="chat-composer__chip-remove"
                :aria-label="`Remover ${user.name}`"
                @click="toggleUser(user.id)"
              >
                <i class="fas fa-times" aria-hidden="true"></i>
              </button>
            </span>

            <button
              v-if="selectedUsers.length > 1"
              type="button"
              class="chat-composer__link-btn"
              @click="draft.recipientIds = []"
            >
              Limpar seleção
            </button>
          </div>

          <label for="chat-composer-search" class="chat-composer__sr">
            Buscar colega por nome ou grupo
          </label>
          <div class="chat-composer__search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
              id="chat-composer-search"
              ref="search"
              v-model="search"
              type="search"
              class="chat-composer__search-input"
              placeholder="Buscar por nome ou grupo"
              autocomplete="off"
            />
          </div>

          <div class="chat-composer__list" aria-live="polite">
            <p v-if="loadingRecipients" class="chat-composer__list-state">
              <i class="fas fa-spinner fa-spin" aria-hidden="true"></i>
              Carregando equipe…
            </p>

            <p
              v-else-if="!groupedUsers.length"
              class="chat-composer__list-state"
            >
              {{
                search
                  ? `Ninguém encontrado para "${search}".`
                  : "Nenhum colega ativo disponível."
              }}
            </p>

            <!--
              Agrupado por grupo de usuário (Lei de Hick):
              escolher entre 5 grupos e depois 6 nomes é mais rápido
              que varrer 30 nomes soltos.
            -->
            <template v-else>
              <div
                v-for="group in groupedUsers"
                :key="group.name"
                class="chat-composer__group"
              >
                <label class="chat-composer__group-header">
                  <input
                    type="checkbox"
                    :checked="isGroupFull(group)"
                    :indeterminate.prop="isGroupPartial(group)"
                    @change="toggleGroup(group)"
                  />
                  <span class="chat-composer__group-name">{{
                    group.name
                  }}</span>
                  <span class="chat-composer__group-count">
                    {{ group.users.length }}
                  </span>
                </label>

                <label
                  v-for="user in group.users"
                  :key="user.id"
                  :class="[
                    'chat-composer__user',
                    { 'chat-composer__user--checked': isSelected(user.id) },
                  ]"
                >
                  <input
                    type="checkbox"
                    :checked="isSelected(user.id)"
                    @change="toggleUser(user.id)"
                  />
                  <span>{{ user.name }}</span>
                </label>
              </div>
            </template>
          </div>
        </div>
      </fieldset>

      <!-- ── 2. MENSAGEM ───────────────────────────────────────────── -->
      <div class="chat-composer__section">
        <p id="chat-composer-body-label" class="chat-composer__legend">
          <span class="chat-composer__step" aria-hidden="true">2</span>
          Mensagem
        </p>
        <p id="chat-composer-body-hint" class="chat-composer__hint">
          Digite ou use o microfone para falar.
        </p>

        <!--
          Reaproveita o componente de voz que a equipe já usa nas evoluções.
          Mesmo gesto, mesmo resultado: zero aprendizado novo.
          Os eventos de teclado/colar servem para detectar se o texto veio
          da voz (ver onBodyChange).
        -->
        <div
          ref="bodyWrapper"
          class="chat-composer__body"
          role="group"
          aria-labelledby="chat-composer-body-label"
          aria-describedby="chat-composer-body-hint chat-composer-counter"
          @keydown.capture="markManualInput"
          @paste.capture="markManualInput"
          @drop.capture="markManualInput"
        >
          <ChromeSpeechToTextTextarea
            v-model="draft.body"
            rows="4"
            max-rows="8"
            placeholder="Ex.: Sra. Maria do quarto 12 recusou o almoço. Favor avaliar."
          />
        </div>

        <div class="chat-composer__body-footer">
          <!-- Texto ditado: lembrete de revisão (prevenção de erro) -->
          <span
            v-if="draft.origin === 'VOICE' && draft.body"
            class="chat-composer__voice-check"
          >
            <i class="fas fa-microphone" aria-hidden="true"></i>
            Texto ditado. Confira nomes, medicamentos e doses antes de enviar.
          </span>

          <span
            id="chat-composer-counter"
            :class="[
              'chat-composer__counter',
              { 'chat-composer__counter--over': bodyLength > maxLength },
            ]"
          >
            {{ bodyLength }} / {{ maxLength }}
          </span>
        </div>
      </div>

      <!-- ── Erros (padrão visual do sistema) ──────────────────────── -->
      <div role="alert">
        <ErrorsMessages :errors="errors" />
      </div>
    </div>

    <!-- ── 3. ENVIAR ─────────────────────────────────────────────── -->
    <template #modal-footer>
      <span class="chat-composer__shortcut">
        <kbd>Ctrl</kbd> + <kbd>Enter</kbd> envia
      </span>

      <button
        type="button"
        class="chat-composer__btn chat-composer__btn--secondary"
        :disabled="sending"
        @click="close"
      >
        Cancelar
      </button>

      <button
        type="button"
        class="chat-composer__btn chat-composer__btn--primary"
        :disabled="sending"
        @click="submit"
      >
        <i
          :class="['fas', sending ? 'fa-spinner fa-spin' : 'fa-paper-plane']"
          aria-hidden="true"
        ></i>
        {{ sending ? "Enviando…" : sendLabel }}
      </button>
    </template>
  </b-modal>
</template>

<script>
import ChromeSpeechToTextTextarea from "../Components/ChromeSpeechToTextTextarea";
import ErrorsMessages from "../Components/ErrorsMessages";

import toast from "../../lib/ToastNotification";
import chatService, { BODY_MAX_LENGTH } from "../../services/chatService";

// Tempo sem teclado/colar após o qual um texto novo é considerado ditado
const VOICE_DETECTION_MS = 1500;

function normalize(text) {
  return String(text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

export default {
  name: "ChatComposer",

  components: { ChromeSpeechToTextTextarea, ErrorsMessages },

  data() {
    return {
      chat: chatService.state,
      search: "",
      errors: [],
      lastManualInputAt: 0,
      maxLength: BODY_MAX_LENGTH,
    };
  },

  computed: {
    composer() {
      return this.chat.composer;
    },

    draft() {
      return this.chat.composer.draft;
    },

    replyTo() {
      return this.chat.composer.replyTo;
    },

    recipients() {
      return this.chat.recipients;
    },

    loadingRecipients() {
      return this.chat.recipientsLoading && !this.recipients.length;
    },

    sending() {
      return this.chat.sending;
    },

    bodyLength() {
      return (this.draft.body || "").length;
    },

    selectedUsers() {
      const ids = this.draft.recipientIds;
      return this.recipients.filter((u) => ids.includes(u.id));
    },

    groupedUsers() {
      const term = normalize(this.search.trim());
      const groups = {};

      this.recipients.forEach((user) => {
        const groupName = user.user_group_name || "Sem grupo";
        const matches =
          !term ||
          normalize(user.name).includes(term) ||
          normalize(groupName).includes(term);
        if (!matches) return;
        (groups[groupName] = groups[groupName] || []).push(user);
      });

      return Object.keys(groups)
        .sort((a, b) => a.localeCompare(b, "pt-BR"))
        .map((name) => ({ name, users: groups[name] }));
    },

    // O botão diz exatamente o que vai acontecer (evita envio errado)
    sendLabel() {
      if (this.draft.mode === "ALL") {
        return this.recipients.length
          ? `Enviar para toda a equipe (${this.recipients.length})`
          : "Enviar para toda a equipe";
      }
      const total = this.draft.recipientIds.length;
      if (total === 0) return "Enviar";
      if (total === 1 && this.selectedUsers[0])
        return `Enviar para ${this.selectedUsers[0].name.split(" ")[0]}`;
      return `Enviar para ${total} pessoas`;
    },
  },

  watch: {
    "draft.body"(newValue, oldValue) {
      this.onBodyChange(newValue || "", oldValue || "");
    },

    // Corrigiu o problema? O erro some na hora.
    "draft.recipientIds"() {
      if (this.errors.length) this.errors = this.validate();
    },

    "draft.mode"() {
      if (this.errors.length) this.errors = this.validate();
    },
  },

  methods: {
    excerpt(text) {
      const clean = String(text || "")
        .replace(/\s+/g, " ")
        .trim();
      return clean.length > 120 ? `${clean.slice(0, 120)}…` : clean;
    },

    // ── Abertura / fechamento ──────────────────────────────────────
    onVisibleChange(isVisible) {
      if (!isVisible) {
        // Fechar NÃO apaga o rascunho: reabrir continua de onde parou
        chatService.closeComposer();
        this.errors = [];
        this.search = "";
      }
    },

    close() {
      chatService.closeComposer();
    },

    focusFirstField() {
      this.$nextTick(() => {
        // Resposta: destinatário já definido, foco vai direto ao texto
        if (this.replyTo || this.draft.mode === "ALL") {
          const field =
            this.$refs.bodyWrapper &&
            this.$refs.bodyWrapper.querySelector("textarea");
          if (field) return field.focus();
        }
        if (this.$refs.search) this.$refs.search.focus();
      });
    },

    cancelReply() {
      this.chat.composer.replyTo = null;
      this.draft.recipientIds = [];
      this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
    },

    // ── Seleção ────────────────────────────────────────────────────
    isSelected(id) {
      return this.draft.recipientIds.includes(id);
    },

    toggleUser(id) {
      const ids = this.draft.recipientIds;
      this.draft.recipientIds = ids.includes(id)
        ? ids.filter((x) => x !== id)
        : [...ids, id];
    },

    isGroupFull(group) {
      return group.users.every((u) => this.isSelected(u.id));
    },

    isGroupPartial(group) {
      const selected = group.users.filter((u) => this.isSelected(u.id)).length;
      return selected > 0 && selected < group.users.length;
    },

    toggleGroup(group) {
      const groupIds = group.users.map((u) => u.id);
      if (this.isGroupFull(group)) {
        this.draft.recipientIds = this.draft.recipientIds.filter(
          (id) => !groupIds.includes(id)
        );
      } else {
        this.draft.recipientIds = [
          ...new Set([...this.draft.recipientIds, ...groupIds]),
        ];
      }
    },

    // ── Origem do texto (digitado x ditado) ────────────────────────
    markManualInput() {
      this.lastManualInputAt = Date.now();
    },

    /**
     * Heurística: o texto cresceu sem tecla, colar ou arrastar recente
     * → veio do microfone. Ao apagar tudo, volta a ser "TEXT".
     * Substituir por um evento do ChromeSpeechToTextTextarea, se ele tiver.
     */
    onBodyChange(newValue, oldValue) {
      if (!newValue.trim()) {
        this.draft.origin = "TEXT";
        return;
      }
      const grew = newValue.length > oldValue.length;
      const noManualInput =
        Date.now() - this.lastManualInputAt > VOICE_DETECTION_MS;
      if (grew && noManualInput) this.draft.origin = "VOICE";

      if (this.errors.length) this.errors = this.validate();
    },

    // ── Validação e envio ──────────────────────────────────────────
    validate() {
      const errors = [];
      const body = (this.draft.body || "").trim();

      if (this.draft.mode === "PEOPLE" && !this.draft.recipientIds.length)
        errors.push("Escolha pelo menos uma pessoa ou marque Toda a equipe.");
      if (!body) errors.push("Escreva ou grave a mensagem.");
      if (body.length > this.maxLength)
        errors.push(
          `A mensagem passou de ${this.maxLength} caracteres. Encurte o texto.`
        );

      return errors;
    },

    async submit() {
      if (this.sending) return;

      this.errors = this.validate();
      if (this.errors.length) return;

      const result = await chatService.sendDraft();

      if (result.ok) {
        this.errors = [];
        toast(result.message || "MENSAGEM ENVIADA", "success");
      } else {
        // Falhou: modal continua aberto e o texto continua lá
        this.errors = result.errors;
      }
    },
  },
};
</script>

<style scoped>
.chat-composer {
  --cc-primary: #0f65b0;
  --cc-primary-dark: #0b4f8a;
  --cc-primary-soft: #eff6ff;
  --cc-text: #1e293b;
  --cc-text-muted: #475569;
  --cc-border: #cbd5e1;
  --cc-border-soft: #e2e8f0;
  --cc-surface-alt: #f8fafc;
  --cc-warning-bg: #fffbeb;
  --cc-warning-border: #f59e0b;
  --cc-warning-text: #78350f;
  --cc-danger: #b91c1c;

  color: var(--cc-text);
  font-size: 16px;
}

/* ── Título ──────────────────────────────────────────────────────────── */
.chat-composer__title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 19px;
  font-weight: 700;
  color: #fff;
}

.chat-composer__title i {
  color: #0f65b0;
}

/* ── Resposta ────────────────────────────────────────────────────────── */
.chat-composer__reply {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 20px;
  padding: 12px 8px 12px 14px;
  border-left: 4px solid var(--cc-primary);
  border-radius: 8px;
  background: var(--cc-primary-soft);
}

.chat-composer__reply-text {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.chat-composer__reply-label {
  font-size: 14px;
  color: var(--cc-text-muted);
}

.chat-composer__reply-quote {
  font-size: 15px;
  color: var(--cc-text);
  quotes: "“" "”";
}

/* ── Seções numeradas ────────────────────────────────────────────────── */
.chat-composer__section {
  margin: 0 0 24px;
  padding: 0;
  border: 0;
  min-width: 0;
}

.chat-composer__legend {
  display: flex;
  align-items: center;
  gap: 10px;
  width: auto;
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 700;
  color: var(--cc-text);
}

.chat-composer__step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--cc-primary);
  color: #ffffff;
  font-size: 13px;
}

.chat-composer__hint {
  margin: -6px 0 8px 36px;
  font-size: 14px;
  color: var(--cc-text-muted);
}

/* ── Modos (pessoas / todos) ─────────────────────────────────────────── */
.chat-composer__modes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.chat-composer__mode {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 64px;
  margin: 0;
  padding: 12px 14px;
  border: 2px solid var(--cc-border);
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.chat-composer__mode:hover {
  border-color: #94a3b8;
}

.chat-composer__mode i {
  font-size: 20px;
  color: var(--cc-text-muted);
}

.chat-composer__mode span {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.chat-composer__mode small {
  font-size: 13px;
  color: var(--cc-text-muted);
}

.chat-composer__mode--active {
  border-color: var(--cc-primary);
  background: var(--cc-primary-soft);
}

.chat-composer__mode--active i {
  color: var(--cc-primary);
}

.chat-composer__mode--all.chat-composer__mode--active {
  border-color: var(--cc-warning-border);
  background: var(--cc-warning-bg);
}

.chat-composer__mode--all.chat-composer__mode--active i {
  color: #b45309;
}

/* Radio nativo escondido visualmente, mas acessível ao teclado */
.chat-composer__mode-input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
}

.chat-composer__mode:focus-within {
  outline: 3px solid rgba(15, 101, 176, 0.45);
  outline-offset: 2px;
}

/* ── Avisos ──────────────────────────────────────────────────────────── */
.chat-composer__notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 12px 0 0;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.5;
}

.chat-composer__notice--warning {
  border: 1px solid var(--cc-warning-border);
  background: var(--cc-warning-bg);
  color: var(--cc-warning-text);
}

.chat-composer__notice i {
  margin-top: 3px;
}

/* ── Pessoas ─────────────────────────────────────────────────────────── */
.chat-composer__people {
  margin-top: 12px;
}

.chat-composer__chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

.chat-composer__chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 2px 2px 12px;
  border-radius: 999px;
  background: var(--cc-primary);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
}

.chat-composer__chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #ffffff;
  cursor: pointer;
}

.chat-composer__chip-remove:hover {
  background: rgba(255, 255, 255, 0.2);
}

.chat-composer__link-btn {
  min-height: 32px;
  padding: 0 8px;
  border: 0;
  background: transparent;
  color: var(--cc-primary);
  font-size: 14px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}

.chat-composer__search {
  position: relative;
}

.chat-composer__search i {
  position: absolute;
  top: 50%;
  left: 14px;
  color: var(--cc-text-muted);
  transform: translateY(-50%);
  pointer-events: none;
}

.chat-composer__search-input {
  width: 100%;
  height: 46px;
  padding: 0 14px 0 40px;
  border: 1px solid var(--cc-border);
  border-radius: 10px;
  font-size: 16px; /* 16px evita zoom automático no iOS */
  color: var(--cc-text);
}

.chat-composer__search-input:focus {
  border-color: var(--cc-primary);
  outline: 3px solid rgba(15, 101, 176, 0.25);
}

.chat-composer__list {
  max-height: 260px;
  margin-top: 8px;
  overflow-y: auto;
  border: 1px solid var(--cc-border-soft);
  border-radius: 10px;
}

.chat-composer__list-state {
  margin: 0;
  padding: 20px;
  text-align: center;
  color: var(--cc-text-muted);
}

.chat-composer__group-header {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 10px 14px;
  border-bottom: 1px solid var(--cc-border-soft);
  background: var(--cc-surface-alt);
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--cc-text-muted);
  cursor: pointer;
}

.chat-composer__group-name {
  flex: 1;
}

.chat-composer__group-count {
  min-width: 24px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--cc-border-soft);
  text-align: center;
}

.chat-composer__user {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px; /* alvo de toque confortável */
  margin: 0;
  padding: 8px 14px 8px 34px;
  border-bottom: 1px solid var(--cc-border-soft);
  font-size: 15px;
  cursor: pointer;
}

.chat-composer__user:hover {
  background: var(--cc-surface-alt);
}

.chat-composer__user--checked {
  background: var(--cc-primary-soft);
  font-weight: 600;
}

.chat-composer__group-header input,
.chat-composer__user input {
  width: 18px;
  height: 18px;
  accent-color: var(--cc-primary);
  cursor: pointer;
}

/* ── Mensagem ────────────────────────────────────────────────────────── */
.chat-composer__body {
  font-size: 16px;
}

.chat-composer__body-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin-top: 6px;
}

.chat-composer__voice-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  background: var(--cc-warning-bg);
  color: var(--cc-warning-text);
  font-size: 13px;
  font-weight: 600;
}

.chat-composer__counter {
  margin-left: auto;
  font-size: 13px;
  color: var(--cc-text-muted);
}

.chat-composer__counter--over {
  color: var(--cc-danger);
  font-weight: 700;
}

/* ── Rodapé ──────────────────────────────────────────────────────────── */
.chat-composer__shortcut {
  margin-right: auto;
  font-size: 13px;
  color: #475569;
}

.chat-composer__shortcut kbd {
  padding: 1px 6px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: #f8fafc;
  color: #1e293b;
  font-size: 12px;
}

.chat-composer__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 20px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}

.chat-composer__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.chat-composer__btn--primary {
  border: 0;
  background: #0f65b0;
  color: #ffffff;
}

.chat-composer__btn--primary:hover:not(:disabled) {
  background: #0b4f8a;
}

.chat-composer__btn--secondary {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #1e293b;
}

.chat-composer__btn--secondary:hover:not(:disabled) {
  background: #f8fafc;
}

.chat-composer__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--cc-text-muted);
  cursor: pointer;
}

.chat-composer__icon-btn:hover {
  background: rgba(15, 101, 176, 0.1);
}

button:focus {
  outline: none;
}

button:focus-visible {
  outline: 3px solid rgba(15, 101, 176, 0.55);
  outline-offset: 2px;
}

.chat-composer__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ── Celular ─────────────────────────────────────────────────────────── */
@media (max-width: 576px) {
  .chat-composer__modes {
    grid-template-columns: 1fr;
  }

  .chat-composer__shortcut {
    display: none; /* sem teclado físico, o atalho é ruído */
  }

  .chat-composer__btn {
    flex: 1;
  }
}
</style>